// Reusable lead capture service.
// Sends to backend /api/leads. Falls back to localStorage when unreachable.

import { trackEvent } from "@/utils/analytics";

const STORAGE_KEY = "zarantech_leads_v1";
const COOLDOWN_KEY = "zarantech_lead_cooldown";
const COOLDOWN_MS = 4000;

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = BACKEND ? `${BACKEND}/api/leads` : "/api/leads";

const ATTRIBUTION_KEY = "zt_first_touch_attribution_v1";

function trackingParams() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
    msclkid: params.get("msclkid") || undefined,
  };
}

function readFirstTouch() {
  if (typeof window === "undefined") return {};
  try {
    const existing = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "null");
    if (existing && existing.landing_page) return existing;
    const firstTouch = {
      landing_page: window.location.href,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      ...trackingParams(),
    };
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(firstTouch));
    return firstTouch;
  } catch {
    return {
      landing_page: window.location.href,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
      ...trackingParams(),
    };
  }
}

// Affiliate referral code from ?ref=. Persisted so it survives navigation
// between landing on the link and submitting a form.
const REF_KEY = "zt_affiliate_ref";
export function readRef() {
  if (typeof window === "undefined") return undefined;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("ref");
    if (fromUrl) {
      localStorage.setItem(REF_KEY, fromUrl);
      return fromUrl;
    }
    return localStorage.getItem(REF_KEY) || undefined;
  } catch {
    return undefined;
  }
}

// Capture attribution as soon as the application bundle loads, before the
// visitor navigates to another route and eventually opens a form.
if (typeof window !== "undefined") {
  readFirstTouch();
  readRef();
}

function detectDevice() {
  if (typeof navigator === "undefined") return "unknown";
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "") ? "mobile" : "desktop";
}

/**
 * Complete source context shared by lead, resource-download, and affiliate forms.
 * First-touch values are persisted so navigating away from the landing page does
 * not erase the campaign or referring-site attribution before submission.
 */
export function getSourceContext() {
  if (typeof window === "undefined") return {};
  const firstTouch = readFirstTouch();
  const currentTracking = trackingParams();
  return {
    page: window.location.pathname,
    source_url: window.location.href,
    landing_page: firstTouch.landing_page,
    referrer: firstTouch.referrer,
    utm_source: currentTracking.utm_source || firstTouch.utm_source,
    utm_medium: currentTracking.utm_medium || firstTouch.utm_medium,
    utm_campaign: currentTracking.utm_campaign || firstTouch.utm_campaign,
    utm_content: currentTracking.utm_content || firstTouch.utm_content,
    utm_term: currentTracking.utm_term || firstTouch.utm_term,
    gclid: currentTracking.gclid || firstTouch.gclid,
    fbclid: currentTracking.fbclid || firstTouch.fbclid,
    msclkid: currentTracking.msclkid || firstTouch.msclkid,
    ref: readRef(),
    device: detectDevice(),
  };
}

export function isOnCooldown() {
  try {
    const last = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
    return Date.now() - last < COOLDOWN_MS;
  } catch {
    return false;
  }
}

// The AI Leadership Training pages' enquiry forms -- formName="leadership-page-enquiry-form"
// on the listing page, "leadership-detail-<slug>" on each program's own page.
// Shared with LeadForm.jsx so both the lead_type tagging here and the
// country-capture logic there stay in sync off one definition.
export function isLeadershipForm(formName) {
  const name = String(formName || "");
  return name === "leadership-page-enquiry-form" || name.startsWith("leadership-detail-");
}

// Home page forms besides the hero/leadership ones above that should also
// capture and send country.
const EXTRA_COUNTRY_FORMS = new Set([
  "home-page-corporate-training-enquiry-form",
  "corporate-page-corporate-training-enquiry-form",
  "home-page-enquiry-form-footer-section",
  "contact-us-enquiry-form",
]);

// Each course detail page's enquiry form -- formName="course-page-<slug>".
function isCourseDetailForm(formName) {
  return String(formName || "").startsWith("course-page-");
}

// The Resources page's two forms -- formName="resource-<slug>" (enquiry) and
// "resource-download-<slug>" (gated download popup). The prefix check covers
// both in one go.
function isResourceForm(formName) {
  return String(formName || "").startsWith("resource-");
}

// Each corporate training track page's own enquiry form --
// formName="corporate-<slug>-training-enquiry" (see CorporateTrackPage.jsx).
// Distinct from EXTRA_COUNTRY_FORMS' "home-page-..."/"corporate-page-..."
// entries, which don't match this prefix+suffix shape.
function isCorporateTrackForm(formName) {
  const name = String(formName || "");
  return name.startsWith("corporate-") && name.endsWith("-training-enquiry");
}

// Forms that should capture and send the selected country. Every other form
// strips country before submit.
export function capturesCountry(formName) {
  const name = String(formName || "");
  return (
    name.toLowerCase().includes("hero") ||
    isLeadershipForm(name) ||
    isCourseDetailForm(name) ||
    isResourceForm(name) ||
    isCorporateTrackForm(name) ||
    EXTRA_COUNTRY_FORMS.has(name)
  );
}

/**
 * Normalize the UI's form state to the backend Lead schema.
 * The UI historically used camelCase and different variant names.
 */
function toBackendPayload(payload, options) {
  const variant = payload.variant || options.formName;
  // Leadership forms are tagged with their own lead_type instead of falling
  // into corporate/individual.
  const isLeadership = isLeadershipForm(options.formName || payload.formName);
  const isCorporate =
    payload.lead_type === "corporate" ||
    payload.trainingType === "Corporate" ||
    variant === "corporate" ||
    (payload.formName || "").includes("corporate");
  // The Graduate Training page's enquiry form -- formName="graduate-training-enquiry-form"
  // (see GraduateTrainingPage.jsx). Its own lead_type so it can be routed to
  // its own spreadsheet in the backend instead of the individual/corporate split.
  const isGraduateTraining =
    payload.lead_type === "graduate_training" ||
    variant === "graduate" ||
    (options.formName || payload.formName) === "graduate-training-enquiry-form";
  // The Workday Practice Test page's enquiry form -- its own lead_type so the
  // admin can report and route these separately from general enquiries.
  const isPracticeTest =
    (options.formName || payload.formName) === "workday-practice-test-enquiry-form";
  // Resource guides/eBooks enquiry and gated-download forms (resource-<slug>
  // and resource-download-<slug>) -- tagged "resource" instead of individual.
  const isResource = isResourceForm(options.formName || payload.formName);
  return {
    lead_type: isGraduateTraining
      ? "graduate_training"
      : isLeadership
        ? "leadership"
        : isCorporate
          ? "corporate"
          : isPracticeTest
            ? "practice_test"
            : isResource
              ? "resource"
              : "individual",
    name: payload.name || "",
    email: payload.email || "",
    phone: payload.phone || "",
    country: payload.country || undefined,
    company: payload.company || undefined,
    college: payload.college || undefined,
    course: payload.course || payload.interestedArea || undefined,
    course_id: payload.courseId || payload.course_id || undefined,
    training_requirement: payload.trainingRequirement || payload.interestedArea || undefined,
    team_size: payload.teamSize || undefined,
    preferred_format: payload.preferredFormat || undefined,
    preferred_timeline: payload.timeline || undefined,
    message: payload.message || undefined,
    form_name: options.formName || payload.formName || "unknown-form",
    website: payload.website || undefined, // honeypot passthrough
    ...getSourceContext(),
  };
}

export async function submitLead(payload, options = {}) {
  if (isOnCooldown()) {
    return { ok: false, reason: "cooldown" };
  }

  const body = toBackendPayload(payload, options);

  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const created = await res.json().catch(() => ({}));
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    trackEvent("form_submitted", { form: body.form_name, channel: "backend" });
    // leadId is used by the booking popup's "Lead ID, if available" field --
    // not present when falling back to the localStorage path below, since
    // there's no server-assigned id in that case.
    return { ok: true, channel: "backend", leadId: created.id || null };
  } catch (err) {
    console.warn("[leadService] Backend unreachable, saving locally.", err);
  }

  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    existing.push({ ...body, ts: new Date().toISOString() });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    trackEvent("form_submitted", { form: body.form_name, channel: "local" });
    return { ok: true, channel: "local" };
  } catch (err) {
    console.error("[leadService] localStorage unavailable", err);
    return { ok: false, reason: "storage-unavailable" };
  }
}

export function getStoredLeads() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}
